import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ApiKeyApiService from '../../ApiServices/SettingApiServices/ApiKeyApiService';
import { useAuth } from "../Auth/AuthContext";

const ApiKeyForm = () => {
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const { authState } = useAuth();
    const role = authState.roles;
    const user = authState.user;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const response = await ApiKeyApiService.getApiKeys(user.id);
            setApiKeys(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch API keys');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDocs = () => {
        const filePath = "/ADX_Reporting_API_Documentation.pdf";
        const link = document.createElement("a");
        link.href = filePath;
        link.download = "ADX_Reporting_API_Documentation.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleGenerateApiKey = async () => {
        try {
            setLoading(true);
            const obj = {
                userId: user.id,
                userName: user.userName,
                keyType: "GetBasicInfo"
            };
            const response = await ApiKeyApiService.generateKey(obj);
            if (response.data.isSuccess) {
                toast.success("API key generated successfully!");
                fetchApiKeys();
            } else {
                toast.error("Something went wrong!");
            }
        } catch (error) {
            toast.error("Error generating API key.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <div className="bg-white p-6 rounded-xl shadow-md max-w-3xl mx-auto" data-aos="fade-up">
                <h2 className="text-2xl font-semibold text-gray-800 mb-3">ADX Reporting</h2>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    An API key for ADX reporting has not been generated. To enable programmatic access, click <strong>Generate API Key</strong>. Documentation is also available for reference.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <button
                        onClick={handleViewDocs}
                        className="px-5 py-2 text-sm font-medium text-blue-600 border border-blue-500 rounded-md hover:bg-blue-500 hover:text-white transition"
                    >
                        📄 View Docs
                    </button>
                    <button
                        onClick={handleGenerateApiKey}
                        disabled={loading}
                        className={`px-5 py-2 text-sm font-medium rounded-md transition ${
                            loading
                                ? 'bg-yellow-300 text-white cursor-not-allowed'
                                : 'text-yellow-600 border border-yellow-500 hover:bg-yellow-500 hover:text-white'
                        }`}
                    >
                        🔑 {loading ? 'Generating...' : 'Generate API Key'}
                    </button>
                </div>

                <div className="mt-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Generated API Keys</h3>

                    {loading ? (
                        <p className="text-sm text-gray-500">Loading API keys...</p>
                    ) : apiKeys.length > 0 ? (
                        <div className="space-y-4 bg-gray-50 p-4 rounded-lg border">
                            {apiKeys.map((apiKey, index) => (
                                <div key={index} className="bg-white p-3 rounded-md shadow-sm border">
                                    <p className="text-sm text-gray-800">
                                        <strong>🔐 Key {index + 1}:</strong><br />
                                        <span className="break-all text-gray-600">{apiKey.key}</span>
                                    </p>
                                    <p className="text-sm text-gray-800 mt-2">
                                        <strong>🌐 URL {index + 1}:</strong><br />
                                        <span className="break-all text-gray-600">{baseUrl + apiKey.url}</span>
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No API keys found.</p>
                    )}
                </div>
            </div>
        </>
    );
};

export default ApiKeyForm;
